import { useLayoutEffect, useRef, useState } from "react";
import { Picker } from "emoji-mart";
import { useSelector} from 'react-redux';
import { GlobalState, MessageRatingType } from '../../../../store/types';
import cn from "classnames";
import {isMobile} from 'react-device-detect';

import Header from "./components/Header";
import Messages from "./components/Messages";
import Sender from "./components/Sender";
import Footer from "./components/Footer";
import BottomButtonBar from "./components/BottomButtonBar";
import WelcomeBanner from "./components/WelcomeBanner";

//import QuickButtons from "./components/QuickButtons";

import { AnyFunction } from "../../../../utils/types";

import "./style.scss";
import MessageRatingCommentPopup from './components/MessageRatingCommentPopup';
import { putMessageRating } from 'src/store/dispatcher';
import { QuickReplyConfig } from 'src/utils/widgetSettings';
import Loader from 'react-loader-spinner';

interface ISenderRef {
  onSelectEmoji: (event: any) => void;
}

type Props = {
  title: string;
  subtitle: string;
  senderPlaceHolder: string;
  showCloseButton: boolean;
  disabledInput: boolean;
  autofocus: boolean;
  className: string;
  sendMessage: AnyFunction;
  sendCustomComponent: AnyFunction;
  toggleChat: AnyFunction;
  profileAvatar?: string;
  titleAvatar?: string;
  onInteractiveButtonClicked?: AnyFunction;
  onHandleConversationScroll?: AnyFunction;
  onTextInputChange?: (event: any) => void;
  sendButtonAlt: string;
  showTimeStamp: boolean;
  emojis?: boolean;
  fullScreenMode: boolean;
  /** IMBEE-8069 (T86) Areas A and C. Absent for a channel without the feature. */
  quickReplyConfig?: QuickReplyConfig;
};

function Conversation({
  title,
  subtitle,
  senderPlaceHolder,
  showCloseButton,
  disabledInput,
  autofocus,
  className,
  sendMessage,
  sendCustomComponent,
  toggleChat,
  quickReplyConfig,
  profileAvatar,
  titleAvatar,
  onInteractiveButtonClicked,
  onHandleConversationScroll,
  onTextInputChange,
  sendButtonAlt,
  showTimeStamp,
  emojis,
  fullScreenMode
}: Props) {

  const parentUrl = useSelector((state: GlobalState) => state.responses.parentUrl);
  const messageRatingNegativeCommentPrompt = useSelector((state: GlobalState) => state.messages.ratingNegativeCommentPrompt);
  const messageRatingLoading = useSelector((state: GlobalState) => state.messages.ratingLoading);

  const [pickerOffset, setOffset] = useState(0);
  const senderRef = useRef<ISenderRef>(null!);
  const [pickerStatus, setPicket] = useState(false);
  const [showRatingCommentPopup, setShowRatingCommentPopup] = useState(false);
  const [ratingCommentPopupData, setRatingCommentPopupData] = useState<{type: MessageRatingType, messageId: string, prompt: string} | null>(null);

  const themeColor = useSelector((state: GlobalState) => state.responses.themeColor);

  /**
   * IMBEE-8069 UAT — Areas A and C stay out of the way until the visitor has a session.
   *
   * Both send the button's label as a visitor message, and a message can only be sent once the
   * contact form has been submitted and createSession has run. Clicked before that, the widget
   * POSTed /message with an EMPTY session_id: the button appeared to work, nothing reached the
   * platform, and the visitor got no error. Hiding the surfaces is the honest form of that.
   *
   * showContactForm is the state to read because it is the only state there is: the widget never
   * reads a contact-form config flag. `enableContactForm` does not appear anywhere in this repo —
   * `getWidget` does not request it and `renderContactForm()` renders the form for every
   * non-member visitor regardless of what the channel configured. So this gate means exactly
   * "hide while the form is up", and nothing more.
   *
   * It is false for member mode (App.tsx:454-458) and for a returning visitor whose session was
   * restored (App.tsx:630-633), and it flips false on submit — so the surfaces appear in all the
   * cases they should.
   *
   * What it does NOT do, and an earlier version of this comment wrongly claimed it did, is
   * recognise a channel whose operator turned the pre-chat form off. The widget shows that form
   * anyway — a pre-existing gap, not one this change introduced — and Areas A and C now wait
   * behind a form that arguably should not be there. Tracked as IMBEE-9559, which also carries
   * the product question: should the banner appear immediately on such a channel?
   */
  const showContactForm = useSelector((state: GlobalState) => state.behavior.showContactForm);

  const onSelectEmoji = (emoji) => {
    senderRef.current?.onSelectEmoji(emoji);
  };

  const togglePicker = () => {
    setPicket((prevPickerStatus) => !prevPickerStatus);
  };

  /**
   * IMBEE-8069 — the widget's height comes from the message list's fixed height, so the
   * welcome banner and the bottom button bar would simply make the whole widget taller and
   * push the composer out of the frame. Measure what they occupy and hand it to Messages to
   * subtract. Measured, not assumed: the banner grows and shrinks with its button count, its
   * image and its pager, and the bar disappears when every button is filtered out.
   */
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const bottomBarRef = useRef<HTMLDivElement | null>(null);
  const [reservedHeight, setReservedHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () =>
      setReservedHeight(
        (bannerRef.current?.offsetHeight ?? 0) + (bottomBarRef.current?.offsetHeight ?? 0),
      );
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    if (bannerRef.current) observer.observe(bannerRef.current);
    if (bottomBarRef.current) observer.observe(bottomBarRef.current);
    return () => observer.disconnect();
    // showContactForm too: the surfaces mount and unmount with it, and the fallback path for
    // browsers without ResizeObserver has nothing else to re-trigger on.
  }, [quickReplyConfig, showContactForm]);

  const handlerSendMsn = (event, event2?, event3?) => {
    sendMessage(event, event2, event3);
    // console.log("event4: ", event4);
    if (pickerStatus) setPicket(false);
  };

  const promptMessageRatingComment = (messageId: string, type: MessageRatingType) => {
    setRatingCommentPopupData({
      type,
      messageId,
      prompt: messageRatingNegativeCommentPrompt,
    });
    setShowRatingCommentPopup(true);
  }

  const handleMessageRatingSubmit = (messageId: string, type: MessageRatingType, comment?: string) => {
    putMessageRating(messageId, type, comment);
  };

  const handleRating = (messageId: string, type: MessageRatingType) => {
    if(messageRatingNegativeCommentPrompt && type === MessageRatingType.NEGATIVE) {
      promptMessageRatingComment(messageId, type);
      return;
    }
    handleMessageRatingSubmit(messageId, type);
  }

  return (
    <div
      className={cn(
        fullScreenMode || isMobile ? 'rcw-conversation-container-fs' : 'rcw-conversation-container',
        className,
      )}
      aria-live="polite"
      onMouseLeave={(e) => {
        window.parent && parentUrl && window.parent.postMessage('isNotFocusing', parentUrl);
      }}
    >
      <Header
        title={title}
        subtitle={subtitle}
        toggleChat={toggleChat}
        showCloseButton={showCloseButton}
        titleAvatar={titleAvatar}
        fullScreenMode={fullScreenMode}
      />
      <div ref={bannerRef}>
        <WelcomeBanner
          enabled={quickReplyConfig?.enableWelcomeBanner && !showContactForm}
          imageMode={quickReplyConfig?.welcomeBannerImageMode}
          imageUrl={quickReplyConfig?.welcomeBannerImageUrl}
          buttons={quickReplyConfig?.welcomeBannerButtons}
          themeColor={themeColor}
          onSelect={sendMessage}
        />
      </div>
      <Messages
        profileAvatar={profileAvatar}
        showTimeStamp={showTimeStamp}
        onInteractiveButtonClicked={onInteractiveButtonClicked}
        onHandleConversationScroll={onHandleConversationScroll}
        fullScreenMode={fullScreenMode || isMobile}
        handleRating={handleRating}
        reservedHeight={reservedHeight}
      />
      {/* <QuickButtons onInteractiveButtonClicked={onInteractiveButtonClicked} /> */}
      {emojis && pickerStatus && (
        <Picker
          style={{
            position: 'absolute',
            bottom: pickerOffset,
            left: '0',
            width: '100%',
          }}
          onSelect={onSelectEmoji}
          exclude={['flags']}
        />
      )}
      <div ref={bottomBarRef}>
        <BottomButtonBar
          enabled={quickReplyConfig?.enableBottomButtons && !showContactForm}
          buttons={quickReplyConfig?.bottomButtons}
          onSelect={sendMessage}
        />
      </div>
      <Sender
        sendMessage={handlerSendMsn}
        sendCustomComponent={sendCustomComponent}
        ref={senderRef}
        placeholder={senderPlaceHolder}
        disabledInput={disabledInput}
        autofocus={autofocus}
        onTextInputChange={onTextInputChange}
        buttonAlt={sendButtonAlt}
        onPressEmoji={togglePicker}
        onChangeSize={setOffset}
      />
      <Footer fullScreenMode={fullScreenMode || isMobile} />
      {showRatingCommentPopup && ratingCommentPopupData && (
        <MessageRatingCommentPopup
          onSubmit={handleMessageRatingSubmit}
          onClose={() => setShowRatingCommentPopup(false)}
          {...ratingCommentPopupData}
        />
      )}
      {messageRatingLoading && (
        <div className={`message-rating-loading-overlay`}>
          <Loader
            type="ThreeDots"
            color={`#${themeColor}`}
            height={100}
            width={100}
          />
        </div>
      )}
    </div>
  );
}

export default Conversation;
